import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import BuscadorDesplegable from '../components/BuscadorDesplegable';
import api from '../services/api';
import { exportarVentasPDF, exportarVentasExcel } from '../services/exportar';
import { useEmpresa } from '../context/EmpresaContext';
import { useAuth } from '../context/AuthContext';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const fmt = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);
const METODOS = ['efectivo','transferencia','nequi','daviplata','pse','otro'];
const itemVacio = { tipo:'servicio', descripcion:'', producto_id:'', cantidad:1, precio_unitario:'', descuento_item:0 };

export default function Ventas() {
  const hoy = new Date();
  const [vista, setVista]     = useState('lista'); // lista | nueva | recibo
  const [mes, setMes]         = useState(hoy.getMonth()+1);
  const [anio, setAnio]       = useState(hoy.getFullYear());
  const [ventas, setVentas]   = useState([]);
  const [productos, setProd]  = useState([]);
  const [clientes, setClients]= useState([]);
  const [resumen, setResumen] = useState({});
  const [buscar, setBuscar]   = useState('');
  const [reciboData, setRecibo]= useState(null);
  const [busqProd, setBusqProd]= useState('');
  const { empresa } = useEmpresa();
  const { usuario } = useAuth();
  // Anular mueve inventario y comisiones de vendedores: es del superadministrador
  const esSuperadmin = usuario?.rol === 'superadmin';
  const reciboRef = useRef(null);

  // Form venta
  const [cliente_id, setClienteId]       = useState('');
  const [cliente_nombre, setClienteNombre]= useState('');
  const [cliente_telefono, setClienteTel] = useState('');
  const [items, setItems]     = useState([{...itemVacio}]);
  const [descuento, setDesc]  = useState(0);
  const [fecha, setFecha]     = useState(hoy.toISOString().split('T')[0]);
  const [metodo, setMetodo]   = useState('efectivo');
  const [notas, setNotas]     = useState('');
  const [error, setError]     = useState('');
  const [guardando, setGuardando] = useState(false);
  // Si trae id, el mismo formulario está editando esa factura en vez de creando una nueva
  const [editandoId, setEditandoId] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [vendedorId, setVendedorId] = useState('');

  const cargar = useCallback(async () => {
    const desde = `${anio}-${String(mes).padStart(2,'0')}-01`;
    const hasta = `${anio}-${String(mes).padStart(2,'0')}-${new Date(anio, mes, 0).getDate()}`;
    const params = new URLSearchParams({desde, hasta});
    if(buscar) params.append('buscar', buscar);
    const [rv, rr] = await Promise.all([
      api.get(`/api/ventas?${params}`),
      api.get(`/api/ventas/resumen?mes=${mes}&anio=${anio}`),
    ]);
    setVentas(rv.data);
    setResumen(rr.data);
  }, [mes, anio, buscar]);

  useEffect(()=>{ cargar(); }, [cargar]);
  useEffect(()=>{
    api.get('/api/catalogo?limit=2000&activo=true').then(r=>setProd(r.data.productos||[]));
    api.get('/api/clientes').then(r=>setClients(r.data));
    api.get('/api/vendedores').then(r=>setVendedores((r.data||[]).filter(v=>v.activo))).catch(()=>{});
  },[]);

  const subtotal = items.reduce((s,i)=>s+(parseFloat(i.precio_unitario||0)*parseInt(i.cantidad||1)-parseFloat(i.descuento_item||0)),0);
  const total = subtotal - parseFloat(descuento||0);

  const abrirNueva = () => {
    setEditandoId(null);
    setClienteId(''); setClienteNombre(''); setClienteTel('');
    setItems([{...itemVacio}]); setDesc(0);
    setFecha(hoy.toISOString().split('T')[0]); setMetodo('efectivo'); setNotas('');
    setError(''); setBusqProd(''); setVista('nueva');
  };

  /**
   * Editar una factura ya registrada: se abre el MISMO formulario con sus datos cargados.
   *
   * Se reutiliza en vez de hacer una pantalla aparte para que no haya dos formularios que
   * mantener —y que un día calculen distinto—. Lo único que cambia es a dónde se guarda.
   */
  const abrirEditar = async (venta) => {
    try {
      const { data } = await api.get(`/api/ventas/${venta.id}`);
      setEditandoId(venta.id);
      setClienteId(data.cliente_id || '');
      setClienteNombre(data.cliente_nombre || data.cliente_nombre_reg || '');
      setClienteTel(data.cliente_telefono || '');
      setItems((data.items || []).map(it => ({
        tipo: it.tipo || 'servicio', descripcion: it.descripcion || '',
        producto_id: it.producto_id || '', cantidad: it.cantidad || 1,
        precio_unitario: it.precio_unitario || '', descuento_item: it.descuento_item || 0,
        catalogo_producto_id: it.catalogo_producto_id || undefined,
      })));
      setDesc(data.descuento || 0);
      setFecha((data.fecha || '').split('T')[0] || hoy.toISOString().split('T')[0]);
      setMetodo(data.metodo_pago || 'efectivo');
      setNotas(data.notas || '');
      setVendedorId(data.vendedor_id || '');
      setError(''); setBusqProd(''); setVista('nueva');
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo abrir la factura');
    }
  };

  const addItem = () => setItems(p=>[...p,{...itemVacio}]);
  const removeItem = i => setItems(p=>p.filter((_,idx)=>idx!==i));
  const updItem = (i,k,v) => setItems(p=>p.map((it,idx)=>idx===i?{...it,[k]:v}:it));

  const onProdSelect = (i, id) => {
    const p = productos.find(x=>x.id===id);
    setItems(prev=>prev.map((it,idx)=>idx===i
      ? {...it, producto_id:id, descripcion:p?p.nombre:it.descripcion, precio_unitario:p?p.precio_venta:it.precio_unitario, tipo:'producto'}
      : it
    ));
  };

  const onClienteSelect = id => {
    setClienteId(id);
    const cl = clientes.find(c=>c.id===id);
    if(cl){ setClienteNombre(cl.nombre); setClienteTel(cl.telefono||''); }
  };

  const guardar = async () => {
    const validos = items.filter(i=>i.descripcion&&i.precio_unitario);
    if(!validos.length){ setError('Agrega al menos un ítem con descripción y precio'); return; }
    setGuardando(true);
    const cuerpo = {
      cliente_id:cliente_id||null, cliente_nombre:cliente_nombre||null,
      cliente_telefono:cliente_telefono||null, items:validos,
      descuento, fecha, metodo_pago:metodo, notas,
      vendedor_id: vendedorId || null
    };
    try {
      // Editando: se avisa el cambio de comisión antes de dejarlo hecho, porque afecta lo
      // que se le paga a alguien y no debería enterarse al liquidar
      if (editandoId) {
        const { data } = await api.put(`/api/ventas/${editandoId}`, cuerpo);
        const cambioComision = data.antes.comision_vendedor !== data.ahora.comision_vendedor;
        alert(`${data.mensaje}.\n\n` +
          `Total: ${fmt(data.antes.total)} → ${fmt(data.ahora.total)}` +
          (cambioComision
            ? `\nComisión del vendedor: ${fmt(data.antes.comision_vendedor)} → ${fmt(data.ahora.comision_vendedor)}`
            : ''));
        setEditandoId(null);
        cargar();
        const rv = await api.get(`/api/ventas/${editandoId}`);
        setRecibo(rv.data);
        setVista('recibo');
        setGuardando(false);
        return;
      }
      const {data} = await api.post('/api/ventas', cuerpo);
      cargar();
      const rv = await api.get(`/api/ventas/${data.id}`);
      setRecibo(rv.data);
      setVista('recibo');
    } catch(e){ setError(e.response?.data?.error||'Error al guardar'); }
    finally{ setGuardando(false); }
  };

  const verRecibo = async id => {
    const {data} = await api.get(`/api/ventas/${id}`);
    setRecibo(data); setVista('recibo');
  };

  /**
   * Anular una factura. No se borra: queda el rastro de que existió y por qué se anuló.
   *
   * Al anular pasan dos cosas que conviene decir en voz alta antes de confirmar: el
   * inventario recupera las unidades y el vendedor deja de cobrar esa comisión.
   */
  const anular = async (venta) => {
    const motivo = window.prompt(
      `Anular la factura ${venta.numero || ''}\n\n` +
      `Se devuelve el inventario y el vendedor deja de cobrar su comisión.\n` +
      `La factura NO se borra: queda registrada como anulada.\n\n` +
      `¿Por qué se anula?`);
    if (motivo === null) return;            // cerró el cuadro
    if (!motivo.trim()) { alert('Hay que escribir el motivo.'); return; }
    try {
      const { data } = await api.put(`/api/ventas/${venta.id}/anular`, { motivo });
      alert(`${data.mensaje}.` +
        (data.comision_retirada ? `\nSe retiró una comisión de $${data.comision_retirada.toLocaleString('es-CO')}.` : '') +
        (data.stock_devuelto ? `\nSe devolvieron ${data.stock_devuelto} producto(s) al inventario.` : ''));
      cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo anular');
    }
  };

  const logoHtml = empresa?.logo_base64
    ? `<img src="data:${empresa.logo_mime||'image/png'};base64,${empresa.logo_base64}" style="height:60px;object-fit:contain;" alt="Logo"/>`
    : `<div style="font-size:24px;font-weight:900;color:#1e3a5f;letter-spacing:-1px;">${empresa?.nombre||'Linkus Telecomunicaciones'}</div>`;

  const imprimirPOS = () => {
    if (!reciboData) return;
    const emp = empresa || {};
    const items = reciboData.items || [];
    const fmt2 = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);
    const lineas = items.map(i =>
      `<tr><td style="padding:2px 0">${i.descripcion}</td><td style="text-align:right;white-space:nowrap">${i.cantidad} x ${fmt2(i.precio_unitario)}</td></tr>
       <tr><td colspan="2" style="text-align:right;font-weight:bold">${fmt2(i.subtotal)}</td></tr>`
    ).join('');
    const w = window.open('','_blank');
    w.document.write(`<html><head><title>Recibo POS</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:'Courier New',monospace;width:72mm;margin:0 auto;font-size:11px;color:#000;}
      .center{text-align:center;} .bold{font-weight:bold;} .line{border-top:1px dashed #000;margin:4px 0;}
      table{width:100%;border-collapse:collapse;} td{padding:1px 0;font-size:11px;}
      .total-row td{font-size:14px;font-weight:bold;border-top:1px solid #000;padding-top:4px;}
      @media print{body{margin:0;width:72mm;}}
    </style></head><body>
    ${emp.logo_base64 ? `<div class="center" style="margin-bottom:4px"><img src="data:${emp.logo_mime||'image/png'};base64,${emp.logo_base64}" style="height:40px;object-fit:contain;" alt="Logo"/></div>` : `<div class="center bold" style="font-size:14px;margin-bottom:2px">${emp.nombre||'Linkus Telecomunicaciones'}</div>`}
    ${emp.direccion?`<div class="center" style="font-size:10px">${emp.direccion}</div>`:''}
    ${emp.telefono?`<div class="center" style="font-size:10px">Tel: ${emp.telefono}</div>`:''}
    ${emp.nit?`<div class="center" style="font-size:10px">NIT: ${emp.nit}</div>`:''}
    <div class="line"></div>
    <div class="bold center">RECIBO DE VENTA</div>
    <div style="font-size:10px">N°: ${reciboData.numero_recibo||'-'}</div>
    <div style="font-size:10px">Fecha: ${new Date(reciboData.fecha||reciboData.creado_en).toLocaleDateString('es-CO')}</div>
    <div style="font-size:10px">Cliente: ${reciboData.cliente_nombre||reciboData.cliente_nombre_reg||'-'}</div>
    ${reciboData.metodo_pago?`<div style="font-size:10px">Pago: ${reciboData.metodo_pago}</div>`:''}
    <div class="line"></div>
    <table>${lineas}</table>
    <div class="line"></div>
    ${reciboData.descuento>0?`<div style="text-align:right;font-size:11px">Descuento: -${fmt2(reciboData.descuento)}</div>`:''}
    <table><tr class="total-row"><td>TOTAL</td><td style="text-align:right">${fmt2(reciboData.total)}</td></tr></table>
    <div class="line"></div>
    ${emp.pie_factura?`<div class="center" style="font-size:10px;margin-top:4px">${emp.pie_factura}</div>`:''}
    <div class="center" style="font-size:10px;margin-top:4px">¡Gracias por su compra!</div>
    <div style="margin-bottom:20px"></div>
    </body></html>`);
    w.document.close();
    setTimeout(()=>w.print(),300);
  };

  const imprimirA4 = () => {
    if (!reciboData) return;
    const emp = empresa || {};
    const items = reciboData.items || [];
    const fmt2 = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);
    const filas = items.map((i,idx) => `
      <tr style="background:${idx%2===0?'#f9f9f9':'#fff'}">
        <td style="padding:8px 10px;border:1px solid #e5e7eb">${i.descripcion}</td>
        <td style="padding:8px 10px;border:1px solid #e5e7eb;text-align:center">${i.cantidad}</td>
        <td style="padding:8px 10px;border:1px solid #e5e7eb;text-align:right">${fmt2(i.precio_unitario)}</td>
        ${i.descuento_item>0?`<td style="padding:8px 10px;border:1px solid #e5e7eb;text-align:right">-${fmt2(i.descuento_item)}</td>`:'<td style="padding:8px 10px;border:1px solid #e5e7eb;text-align:right">—</td>'}
        <td style="padding:8px 10px;border:1px solid #e5e7eb;text-align:right;font-weight:bold">${fmt2(i.subtotal)}</td>
      </tr>`).join('');
    const w = window.open('','_blank');
    w.document.write(`<html><head><title>Recibo ${reciboData.numero_recibo}</title>
    <style>
      body{font-family:Arial,sans-serif;margin:0;padding:30px;color:#111;font-size:13px;}
      .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:30px;}
      .empresa{font-size:22px;font-weight:bold;color:#1e3a5f;}
      .empresa-info{font-size:12px;color:#555;margin-top:4px;line-height:1.6;}
      .doc-info{text-align:right;}
      .doc-titulo{font-size:20px;font-weight:bold;color:#1e3a5f;margin-bottom:6px;}
      .doc-num{font-size:14px;color:#555;}
      .cliente-box{background:#f3f4f6;border-radius:8px;padding:14px 18px;margin-bottom:24px;}
      .cliente-titulo{font-size:11px;font-weight:bold;color:#888;text-transform:uppercase;margin-bottom:6px;}
      .cliente-nombre{font-size:16px;font-weight:bold;color:#1e3a5f;}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;}
      th{background:#1e3a5f;color:#fff;padding:9px 10px;text-align:left;font-size:12px;}
      .totales{display:flex;justify-content:flex-end;}
      .totales-box{width:280px;}
      .tot-row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #e5e7eb;font-size:13px;}
      .tot-final{font-size:16px;font-weight:bold;color:#1e3a5f;border-top:2px solid #1e3a5f;padding-top:8px;}
      .pie{text-align:center;margin-top:40px;font-size:11px;color:#888;border-top:1px solid #e5e7eb;padding-top:16px;}
      @media print{body{padding:15px;}}
    </style></head><body>
    <div class="header">
      <div>
        <div>${emp.logo_base64 ? `<img src="data:${emp.logo_mime||'image/png'};base64,${emp.logo_base64}" style="height:70px;object-fit:contain;margin-bottom:6px;" alt="Logo"/>` : `<div class="empresa">${emp.nombre||'Linkus Telecomunicaciones'}</div>`}</div>
        <div class="empresa-info">
          ${emp.nit?`NIT: ${emp.nit}<br>`:''}
          ${emp.direccion?`${emp.direccion}<br>`:''}
          ${emp.telefono?`Tel: ${emp.telefono}<br>`:''}
          ${emp.email?`${emp.email}`:''}
        </div>
      </div>
      <div class="doc-info">
        <div class="doc-titulo">RECIBO DE VENTA</div>
        <div class="doc-num">N° ${reciboData.numero_recibo||'-'}</div>
        <div class="doc-num">Fecha: ${new Date(reciboData.fecha||reciboData.creado_en).toLocaleDateString('es-CO')}</div>
        ${reciboData.metodo_pago?`<div class="doc-num">Pago: ${reciboData.metodo_pago}</div>`:''}
      </div>
    </div>
    <div class="cliente-box">
      <div class="cliente-titulo">Cliente</div>
      <div class="cliente-nombre">${reciboData.cliente_nombre||reciboData.cliente_nombre_reg||'Cliente general'}</div>
      ${reciboData.cliente_telefono||reciboData.cliente_telefono_reg?`<div style="font-size:12px;color:#555;margin-top:2px">Tel: ${reciboData.cliente_telefono||reciboData.cliente_telefono_reg}</div>`:''}
    </div>
    <table>
      <thead><tr>
        <th style="width:45%">Descripción</th>
        <th style="width:10%;text-align:center">Cant.</th>
        <th style="width:15%;text-align:right">Precio unit.</th>
        <th style="width:15%;text-align:right">Descuento</th>
        <th style="width:15%;text-align:right">Subtotal</th>
      </tr></thead>
      <tbody>${filas}</tbody>
    </table>
    <div class="totales"><div class="totales-box">
      ${reciboData.subtotal>0?`<div class="tot-row"><span>Subtotal</span><span>${fmt2(reciboData.subtotal)}</span></div>`:''}
      ${reciboData.descuento>0?`<div class="tot-row"><span>Descuento</span><span>-${fmt2(reciboData.descuento)}</span></div>`:''}
      <div class="tot-row tot-final"><span>TOTAL</span><span>${fmt2(reciboData.total)}</span></div>
    </div></div>
    ${emp.pie_factura?`<div class="pie">${emp.pie_factura}</div>`:
      '<div class="pie">Gracias por su compra — Linkus Telecomunicaciones</div>'}
    </body></html>`);
    w.document.close();
    setTimeout(()=>w.print(),300);
  };

  const imprimir = imprimirA4;

  const enviarWA = () => {
    if(!reciboData) return;
    const tel = reciboData.cliente_telefono||reciboData.cliente_telefono_reg||'';
    const nom = reciboData.cliente_nombre||reciboData.cliente_nombre_reg||'Cliente';
    const lineas = reciboData.items?.map(i=>`• ${i.descripcion} x${i.cantidad}: ${fmt(i.subtotal)}`).join('\n')||'';
    const txt = `*Recibo ${reciboData.numero_recibo}*\nFecha: ${new Date(reciboData.fecha).toLocaleDateString('es-CO')}\nCliente: ${nom}\n\n${lineas}\n\n*TOTAL: ${fmt(reciboData.total)}*\nMétodo: ${reciboData.metodo_pago}`;
    window.open(`https://wa.me/${tel.replace(/\D/g,'')}?text=${encodeURIComponent(txt)}`, '_blank');
  };

  const prodFiltrados = productos.filter(p =>
    !busqProd || p.nombre.toLowerCase().includes(busqProd.toLowerCase()) || p.sku?.toLowerCase().includes(busqProd.toLowerCase())
  );

  // ── VISTA LISTA ──────────────────────────────────────────────
  if(vista==='lista') return (
    <Layout>
      <div className="pagina" style={s.page}>
        <div className="top-sticky" style={s.topBar}>
          <div>
            <h1 style={s.h1}>Ventas y Servicios</h1>
            <p style={s.sub}>Registro de ventas y servicios</p>
          </div>
          <div style={{display:'flex',gap:'8px'}}>
            <button style={s.btnExp} onClick={()=>exportarVentasExcel(ventas,mes,anio)}>⬇ Excel</button>
            <button style={s.btnExp} onClick={()=>exportarVentasPDF(ventas,resumen,mes,anio,empresa)}>🖨 PDF</button>
            <button style={s.btnPrimary} onClick={abrirNueva}>+ Nueva Venta</button>
          </div>
        </div>

        {/* Selector período */}
        <div style={s.periodo}>
          <span style={s.periodoLabel}>Período:</span>
          <div style={{display:'flex',gap:'4px',flex:1,flexWrap:'wrap'}}>
            {MESES.map((m,i)=>(
              <button key={i} onClick={()=>setMes(i+1)} style={{...s.mesBtn,...(mes===i+1?s.mesBtnOn:{})}}>
                {m.slice(0,3)}
              </button>
            ))}
          </div>
          <select style={s.anioSel} value={anio} onChange={e=>setAnio(+e.target.value)}>
            {[anio-1,anio,anio+1].map(a=><option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        {/* Stats */}
        <div style={s.statsGrid}>
          {[
            // Es un conteo, no plata: decía «Total ventas» junto a los ingresos y se leía
            // como si fuera otra cifra de dinero.
            {icon:'🧾',val:resumen.total_ventas||0,      label:'Número de ventas',color:'#fff'},
            {icon:'💰',val:fmt(resumen.total_ingresos),  label:'Ingresos totales',color:'#22C55E'},
            {icon:'📦',val:fmt(resumen.total_productos), label:'Por productos',   color:'#8B5CF6'},
            {icon:'🔧',val:fmt(resumen.total_servicios), label:'Por servicios',   color:'#00D4FF'},
          ].map(st=>(
            <div key={st.label} style={s.statCard}>
              <span style={{fontSize:'22px'}}>{st.icon}</span>
              <span style={{fontSize:'22px',fontWeight:'800',color:st.color}}>{st.val}</span>
              <span style={{fontSize:'12px',color:'rgba(255,255,255,0.4)'}}>{st.label}</span>
            </div>
          ))}
        </div>

        {/* Buscador */}
        <input style={s.busqInput} placeholder="🔍 Buscar por cliente o N° recibo..." value={buscar} onChange={e=>setBuscar(e.target.value)}/>

        {/* Tabla */}
        <div style={s.tableWrap}>
          {ventas.length===0 ? (
            <div style={s.vacio}>No hay ventas para {MESES[mes-1]} {anio}</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  {['N° Recibo','Fecha','Cliente','Ítems','Total','Método','Acciones'].map(h=>(
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ventas.map(v=>{
                  const anulada = ['anulada','cancelada','cancelado'].includes(v.estado);
                  return (
                  <tr key={v.id} style={{...s.tr, ...(anulada ? {opacity:0.55} : {})}}>
                    <td style={s.td}>
                      <code style={s.codigo}>{v.numero_recibo}</code>
                      {anulada && <span style={s.anulada}>ANULADA</span>}
                    </td>
                    <td style={s.td}><span style={s.fechaTag}>{new Date(v.fecha).toLocaleDateString('es-CO')}</span></td>
                    <td style={s.td}>{v.cliente_nombre||v.cliente_nombre_reg||'—'}</td>
                    <td style={s.td}><span style={s.badge}>{v.num_items} ítem(s)</span></td>
                    <td style={s.td}>
                      {/* Tachado, no escondido: la factura anulada sigue existiendo pero
                          su valor ya no cuenta para nada */}
                      <strong style={{color: anulada ? 'rgba(255,255,255,0.4)' : '#22C55E',
                        textDecoration: anulada ? 'line-through' : 'none'}}>{fmt(v.total)}</strong>
                    </td>
                    <td style={s.td}><span style={{...s.badge,textTransform:'capitalize'}}>{v.metodo_pago}</span></td>
                    <td style={s.td}>
                      <div style={{display:'flex',gap:'6px'}}>
                        <button style={s.btnVer} onClick={()=>verRecibo(v.id)}>🧾 Recibo</button>
                        {/* Editar y anular mueven inventario y comisiones, así que solo el
                            superadministrador. Una anulada ya no se toca. */}
                        {esSuperadmin && !anulada && (
                          <button style={s.btnEdit} onClick={()=>abrirEditar(v)}
                            title="Modificar esta factura">✏️</button>
                        )}
                        {esSuperadmin && !anulada && (
                          <button style={s.btnDel} onClick={()=>anular(v)}
                            title="Anular esta factura">✕</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );

  // ── VISTA NUEVA VENTA ────────────────────────────────────────
  if(vista==='nueva') return (
    <Layout>
      <div className="pagina" style={s.page}>
        <div className="top-sticky" style={s.topBar}>
          <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
            <button style={s.btnBack} onClick={()=>{setEditandoId(null); setVista('lista');}}>← Volver</button>
            <div>
              <h1 style={s.h1}>{editandoId ? 'Modificar factura' : 'Nueva Venta'}</h1>
              <p style={s.sub}>
                {editandoId ? 'Los cambios recalculan la ganancia y la comisión'
                            : 'Completa los datos de la venta'}
              </p>
            </div>
          </div>
          <button style={s.btnPrimary} onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando...' : (editandoId ? '✓ Guardar cambios' : '✓ Guardar venta')}
          </button>
        </div>

        {/* Que quede claro que se está tocando algo ya emitido, no creando algo nuevo */}
        {editandoId && (
          <div style={s.avisoEdit}>
            <b>Estás modificando una factura ya registrada.</b> Al guardar se recalculan la
            ganancia y las tres comisiones con los valores nuevos, y el inventario se ajusta
            por la diferencia. Si cambia lo que se le paga al vendedor, te lo aviso antes de
            terminar.
          </div>
        )}

        <div className="layout-2col" style={s.ventaLayout}>
          {/* Columna izquierda */}
          <div style={{display:'flex',flexDirection:'column',gap:'16px'}}>

            {/* Cliente */}
            <div style={s.card}>
              <h3 style={s.cardTitle}>👤 Cliente</h3>
              <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                <div>
                  <label style={s.label}>Cliente registrado</label>
                  <BuscadorDesplegable
                    opciones={clientes}
                    valor={cliente_id}
                    onChange={onClienteSelect}
                    placeholder="— Seleccionar cliente (opcional) —"
                    formatear={c=>`${c.nombre}${c.cedula?' — '+c.cedula:''}`}
                  />
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <div>
                    <label style={s.label}>Nombre</label>
                    <input style={s.input} value={cliente_nombre} onChange={e=>setClienteNombre(e.target.value)} placeholder="Nombre del cliente"/>
                  </div>
                  <div>
                    <label style={s.label}>Teléfono</label>
                    <input style={s.input} value={cliente_telefono} onChange={e=>setClienteTel(e.target.value)} placeholder="573001234567"/>
                  </div>
                </div>
              </div>
            </div>

            {/* Ítems */}
            <div style={s.card}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px'}}>
                <h3 style={{...s.cardTitle,margin:0}}>🛒 Ítems</h3>
                <button style={s.btnAdd} onClick={addItem}>+ Agregar ítem</button>
              </div>

              {/* Buscador de productos */}
              <input style={{...s.input,marginBottom:'12px'}} placeholder="🔍 Buscar producto por nombre o SKU..." value={busqProd} onChange={e=>setBusqProd(e.target.value)}/>

              {items.map((item,i)=>(
                <div key={i} style={s.itemCard}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                    <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                      <select style={{...s.inputSm,width:'auto'}} value={item.tipo} onChange={e=>updItem(i,'tipo',e.target.value)}>
                        <option value="servicio">🔧 Servicio</option>
                        <option value="producto">📦 Producto</option>
                      </select>
                      <span style={{fontSize:'12px',color:'rgba(255,255,255,0.4)'}}>Ítem {i+1}</span>
                    </div>
                    {items.length>1 && <button style={s.btnRemove} onClick={()=>removeItem(i)}>✕ Eliminar</button>}
                  </div>

                  {item.tipo==='producto' && (
                    <div style={{marginBottom:'8px'}}>
                      <label style={s.label}>Seleccionar del catálogo</label>
                      <BuscadorDesplegable
                        opciones={productos}
                        valor={item.producto_id}
                        onChange={id=>onProdSelect(i,id)}
                        placeholder="— Buscar en catálogo —"
                        formatear={p=>`[${p.sku}] ${p.nombre} — ${fmt(p.precio_venta)}`}
                      />
                    </div>
                  )}

                  <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                    <div>
                      <label style={s.label}>Descripción</label>
                      <input style={s.input} value={item.descripcion} onChange={e=>updItem(i,'descripcion',e.target.value)} placeholder="Descripción del producto o servicio"/>
                    </div>
                    <div className="grid-4campos" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'8px'}}>
                      <div>
                        <label style={s.label}>Cantidad</label>
                        <input style={s.input} type="number" min="1" value={item.cantidad} onChange={e=>updItem(i,'cantidad',e.target.value)}/>
                      </div>
                      <div>
                        <label style={s.label}>Precio unit.</label>
                        <input style={s.input} type="number" value={item.precio_unitario} onChange={e=>updItem(i,'precio_unitario',e.target.value)} placeholder="0"/>
                      </div>
                      <div>
                        <label style={s.label}>Descuento</label>
                        <input style={s.input} type="number" min="0" value={item.descuento_item} onChange={e=>updItem(i,'descuento_item',e.target.value)} placeholder="0"/>
                      </div>
                      <div>
                        <label style={s.label}>Subtotal</label>
                        <div style={{...s.input, background:'rgba(0,229,255,0.05)', color:'#00E5FF', fontWeight:'700', display:'flex', alignItems:'center'}}>
                          {new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(((item.precio_unitario||0)*(item.cantidad||1))-(item.descuento_item||0))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{textAlign:'right',marginTop:'8px',fontSize:'14px',fontWeight:'700',color:'#22C55E'}}>
                    Subtotal: {fmt(parseFloat(item.precio_unitario||0)*parseInt(item.cantidad||1)-parseFloat(item.descuento_item||0))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Columna derecha */}
          <div style={{display:'flex',flexDirection:'column',gap:'16px'}}>

            {/* Resumen */}
            <div style={s.card}>
              <h3 style={s.cardTitle}>💰 Resumen</h3>
              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                {items.map((it,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'rgba(255,255,255,0.6)'}}>
                    <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginRight:'8px'}}>{it.descripcion||`Ítem ${i+1}`}</span>
                    <span>{fmt(parseFloat(it.precio_unitario||0)*parseInt(it.cantidad||1)-parseFloat(it.descuento_item||0))}</span>
                  </div>
                ))}
                <div style={{borderTop:'1px solid rgba(255,255,255,0.08)',paddingTop:'8px',marginTop:'4px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'rgba(255,255,255,0.6)',marginBottom:'4px'}}>
                    <span>Subtotal</span><span>{fmt(subtotal)}</span>
                  </div>
                  {parseFloat(descuento)>0 && (
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'#FCA5A5',marginBottom:'4px'}}>
                      <span>Descuento</span><span>-{fmt(descuento)}</span>
                    </div>
                  )}
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:'20px',fontWeight:'800',color:'#22C55E',marginTop:'8px'}}>
                    <span>TOTAL</span><span>{fmt(total)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pago */}
            <div style={s.card}>
              <h3 style={s.cardTitle}>💳 Pago</h3>
              <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                <div>
                  <label style={s.label}>Fecha</label>
                  <input style={s.input} type="date" value={fecha} onChange={e=>setFecha(e.target.value)}/>
                </div>
                <div>
                  <label style={s.label}>Método de pago</label>
                  <select style={s.input} value={metodo} onChange={e=>setMetodo(e.target.value)}>
                    {METODOS.map(m=><option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>)}
                  </select>
                </div>
                {vendedores.length > 0 && (
                  <div>
                    <label style={s.label}>Vendedor</label>
                    <select style={s.input} value={vendedorId} onChange={e=>setVendedorId(e.target.value)}>
                      <option value="">— Sin asignar —</option>
                      {vendedores.map(v=><option key={v.id} value={v.id}>{v.nombre}</option>)}
                    </select>
                    <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.35)', marginTop:'4px' }}>
                      Define a quién le corresponde la comisión de esta venta
                    </div>
                  </div>
                )}
                <div>
                  <label style={s.label}>Descuento general</label>
                  <input style={s.input} type="number" min="0" value={descuento} onChange={e=>setDesc(e.target.value)} placeholder="0"/>
                </div>
                <div>
                  <label style={s.label}>Notas</label>
                  <textarea style={{...s.input,minHeight:'70px',resize:'vertical'}} value={notas} onChange={e=>setNotas(e.target.value)} placeholder="Observaciones adicionales"/>
                </div>
              </div>
            </div>

            {error && <div style={s.errorBox}>{error}</div>}

            <button style={{...s.btnPrimary,width:'100%',padding:'14px',fontSize:'16px'}} onClick={guardar} disabled={guardando}>
              {guardando?'Guardando...':'✓ Guardar y ver recibo'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );

  // ── VISTA RECIBO ─────────────────────────────────────────────
  return (
    <Layout>
      <div className="pagina" style={s.page}>
        <div className="top-sticky" style={s.topBar}>
          <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
            <button style={s.btnBack} onClick={()=>setVista('lista')}>← Volver a ventas</button>
            <h1 style={s.h1}>Recibo {reciboData?.numero_recibo}</h1>
          </div>
          <div style={{display:'flex',gap:'8px'}}>
            <button style={s.btnImp} onClick={imprimirA4}>🖨️ Hoja A4</button>
            <button style={{...s.btnImp, background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.3)', color:'#FCD34D'}} onClick={imprimirPOS}>🧾 Rollo POS</button>
            <button style={s.btnWA} onClick={enviarWA}>📱 WhatsApp</button>
            <button style={s.btnPrimary} onClick={abrirNueva}>+ Nueva Venta</button>
          </div>
        </div>

        <div style={{maxWidth:'480px',margin:'0 auto'}}>
          <div ref={reciboRef} style={s.reciboBox}>
            {empresa?.logo_base64 && (
              <img src={`data:${empresa.logo_mime||'image/png'};base64,${empresa.logo_base64}`} alt="logo" style={{width:'60px',height:'60px',objectFit:'contain',borderRadius:'8px',display:'block',margin:'0 auto 8px'}}/>
            )}
            <h2 style={{textAlign:'center',fontSize:'18px',fontWeight:'800',marginBottom:'2px'}}>{empresa?.nombre||'LINKUS TELECOMUNICACIONES'}</h2>
            {empresa?.nit && <p style={s.reciboInfo}>NIT: {empresa.nit}</p>}
            {empresa?.telefono && <p style={s.reciboInfo}>Tel: {empresa.telefono}</p>}
            {empresa?.direccion && <p style={s.reciboInfo}>{empresa.direccion}</p>}
            <p style={{...s.reciboInfo,marginBottom:'12px',letterSpacing:'1px',textTransform:'uppercase',fontSize:'11px'}}>RECIBO DE VENTA</p>
            <div style={s.reciboLinea}/>
            <div style={s.reciboFila}><span>N° Recibo:</span><strong>{reciboData?.numero_recibo}</strong></div>
            <div style={s.reciboFila}><span>Fecha:</span><span>{reciboData?.fecha&&new Date(reciboData.fecha).toLocaleDateString('es-CO')}</span></div>
            <div style={s.reciboFila}><span>Cliente:</span><span>{reciboData?.cliente_nombre||reciboData?.cliente_nombre_reg||'Consumidor final'}</span></div>
            {(reciboData?.cliente_telefono||reciboData?.cliente_telefono_reg)&&(
              <div style={s.reciboFila}><span>Teléfono:</span><span>{reciboData?.cliente_telefono||reciboData?.cliente_telefono_reg}</span></div>
            )}
            <div style={s.reciboLinea}/>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr>
                  <th style={s.reciboTh}>Descripción</th>
                  <th style={{...s.reciboTh,textAlign:'center'}}>Cant.</th>
                  <th style={{...s.reciboTh,textAlign:'right'}}>Precio</th>
                  <th style={{...s.reciboTh,textAlign:'right'}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {reciboData?.items?.map((it,i)=>(
                  <tr key={i}>
                    <td style={s.reciboTd}>{it.descripcion}</td>
                    <td style={{...s.reciboTd,textAlign:'center'}}>{it.cantidad}</td>
                    <td style={{...s.reciboTd,textAlign:'right'}}>{fmt(it.precio_unitario)}</td>
                    <td style={{...s.reciboTd,textAlign:'right'}}>{fmt(it.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={s.reciboLinea}/>
            <div style={s.reciboFila}><span>Subtotal:</span><span>{fmt(reciboData?.subtotal)}</span></div>
            {parseFloat(reciboData?.descuento)>0&&(
              <div style={{...s.reciboFila,color:'#FCA5A5'}}><span>Descuento:</span><span>-{fmt(reciboData?.descuento)}</span></div>
            )}
            <div style={{...s.reciboFila,fontSize:'18px',fontWeight:'800',marginTop:'8px'}}>
              <span>TOTAL:</span><span style={{color:'#22C55E'}}>{fmt(reciboData?.total)}</span>
            </div>
            <div style={s.reciboLinea}/>
            <div style={s.reciboFila}><span>Método de pago:</span><span style={{textTransform:'capitalize'}}>{reciboData?.metodo_pago}</span></div>
            {reciboData?.notas&&<div style={{fontSize:'12px',color:'rgba(255,255,255,0.4)',marginTop:'8px'}}>Nota: {reciboData.notas}</div>}
            <p style={{textAlign:'center',color:'rgba(255,255,255,0.3)',fontSize:'11px',marginTop:'16px'}}>{empresa?.pie_factura||'¡Gracias por su preferencia!'}</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const s = {
  page:    { padding:'24px', maxWidth:'1280px', margin:'0 auto' },
  topBar:  { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px', flexWrap:'wrap', gap:'12px' },
  h1:      { fontSize:'22px', fontWeight:'700', margin:0, color:'#fff' },
  sub:     { fontSize:'13px', color:'rgba(255,255,255,0.4)', margin:'4px 0 0' },
  btnPrimary: { background:'linear-gradient(135deg,#00D4FF,#4F46E5)', border:'none', borderRadius:'9px', padding:'10px 20px', color:'#fff', fontSize:'14px', fontWeight:'700', cursor:'pointer' },
  btnBack: { background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'8px 14px', color:'rgba(255,255,255,0.7)', fontSize:'13px', cursor:'pointer' },
  btnExp:  { background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'8px', padding:'8px 14px', color:'#86EFAC', fontSize:'13px', cursor:'pointer', fontWeight:'600' },
  btnImp:  { background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'8px 14px', color:'#fff', fontSize:'13px', cursor:'pointer' },
  btnWA:   { background:'rgba(37,211,102,0.15)', border:'1px solid rgba(37,211,102,0.3)', borderRadius:'8px', padding:'8px 14px', color:'#25D366', fontSize:'13px', cursor:'pointer', fontWeight:'600' },
  btnAdd:  { background:'rgba(0,212,255,0.1)', border:'1px solid rgba(0,212,255,0.25)', borderRadius:'7px', padding:'6px 14px', color:'#00D4FF', fontSize:'13px', cursor:'pointer', fontWeight:'600' },
  btnRemove:{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'6px', padding:'5px 10px', color:'#FCA5A5', fontSize:'12px', cursor:'pointer' },
  btnVer:  { background:'rgba(0,212,255,0.1)', border:'none', borderRadius:'6px', padding:'5px 10px', color:'#00D4FF', fontSize:'12px', cursor:'pointer', fontWeight:'600' },
  btnDel:  { background:'rgba(239,68,68,0.1)', border:'none', borderRadius:'6px', padding:'5px 8px', color:'#FCA5A5', fontSize:'12px', cursor:'pointer' },
  anulada: { fontSize:'9.5px', fontWeight:'700', color:'#FCA5A5', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', padding:'1px 6px', borderRadius:'8px', marginLeft:'7px', letterSpacing:'0.5px' },
  btnEdit: { background:'rgba(251,191,36,0.1)', border:'none', borderRadius:'6px', padding:'5px 8px', color:'#FDE047', fontSize:'12px', cursor:'pointer' },
  avisoEdit:{ background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.25)', borderRadius:'10px', padding:'12px 15px', marginBottom:'14px', fontSize:'13px', color:'#FDE047', lineHeight:'1.6' },
  // Período
  periodo: { display:'flex', alignItems:'center', gap:'12px', marginBottom:'20px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'12px 16px' },
  periodoLabel: { fontSize:'13px', color:'rgba(255,255,255,0.4)', whiteSpace:'nowrap' },
  mesBtn:  { background:'none', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'6px', padding:'4px 10px', color:'rgba(255,255,255,0.5)', fontSize:'12px', cursor:'pointer' },
  mesBtnOn:{ background:'rgba(0,212,255,0.15)', border:'1px solid rgba(0,212,255,0.4)', color:'#00D4FF', fontWeight:'700' },
  anioSel: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'6px 12px', color:'#fff', fontSize:'13px', outline:'none' },
  // Stats
  statsGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'12px', marginBottom:'20px' },
  statCard:{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'12px', padding:'16px', display:'flex', flexDirection:'column', gap:'6px' },
  // Tabla
  busqInput:{ width:'100%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'9px 16px', color:'#fff', fontSize:'14px', outline:'none', boxSizing:'border-box', marginBottom:'16px' },
  tableWrap:{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'14px', overflow:'hidden' },
  table:   { width:'100%', borderCollapse:'collapse' },
  th:      { padding:'11px 14px', textAlign:'left', fontSize:'11px', color:'rgba(255,255,255,0.4)', fontWeight:'700', letterSpacing:'0.5px', textTransform:'uppercase', borderBottom:'1px solid rgba(255,255,255,0.06)', background:'rgba(255,255,255,0.03)' },
  tr:      { borderTop:'1px solid rgba(255,255,255,0.05)' },
  td:      { padding:'12px 14px', verticalAlign:'middle', fontSize:'13px', color:'rgba(255,255,255,0.8)' },
  vacio:   { padding:'60px', textAlign:'center', color:'rgba(255,255,255,0.3)', fontSize:'14px' },
  codigo:  { fontSize:'13px', fontWeight:'700', color:'#00D4FF', fontFamily:'monospace' },
  fechaTag:{ fontSize:'12px', color:'rgba(255,255,255,0.5)' },
  badge:   { fontSize:'12px', color:'rgba(255,255,255,0.5)', background:'rgba(255,255,255,0.06)', padding:'2px 8px', borderRadius:'20px' },
  // Nueva venta
  ventaLayout:{ display:'grid', gridTemplateColumns:'minmax(0,1fr) 320px', gap:'20px', alignItems:'start' },
  card:    { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'14px', padding:'20px' },
  cardTitle:{ fontSize:'14px', fontWeight:'700', color:'rgba(255,255,255,0.7)', marginBottom:'14px', marginTop:0 },
  label:   { fontSize:'11px', fontWeight:'700', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.8px', display:'block', marginBottom:'4px' },
  input:   { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'9px 12px', color:'rgba(255,255,255,0.9)', fontSize:'13px', outline:'none', width:'100%', boxSizing:'border-box', fontFamily:'inherit' },
  inputSm: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'7px 10px', color:'rgba(255,255,255,0.9)', fontSize:'12px', outline:'none', width:'100%', boxSizing:'border-box', fontFamily:'inherit' },
  itemCard:{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'14px', marginBottom:'10px', overflow:'visible', position:'relative', zIndex:'auto' },
  errorBox:{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'10px', color:'#FCA5A5', fontSize:'13px' },
  // Recibo
  reciboBox:{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'14px', padding:'24px' },
  reciboInfo:{ textAlign:'center', fontSize:'12px', color:'rgba(255,255,255,0.5)', margin:'2px 0', lineHeight:'1.5' },
  reciboLinea:{ borderTop:'1px dashed rgba(255,255,255,0.15)', margin:'12px 0' },
  reciboFila:{ display:'flex', justifyContent:'space-between', fontSize:'13px', marginBottom:'5px', color:'rgba(255,255,255,0.8)' },
  reciboTh:{ fontSize:'11px', color:'rgba(255,255,255,0.4)', padding:'4px 0', textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.08)' },
  reciboTd:{ fontSize:'12px', padding:'5px 0', color:'rgba(255,255,255,0.8)' },
};
